import React, { useEffect, useState } from 'react';
import { CheckIcon, StarIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
interface OrderRatingModalProps {
  isOpen: boolean;
  orderNumber: string;
  onClose: () => void;
  onSubmit: (rating: number) => void;
}
export function OrderRatingModal({
  isOpen,
  orderNumber,
  onClose,
  onSubmit
}: OrderRatingModalProps) {
  const shouldReduceMotion = useReducedMotion();
  const [rating, setRating] = useState(0);
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  const submitRating = () => {
    if (rating === 0) return;
    onSubmit(rating);
    onClose();
  };
  return <AnimatePresence data-id="element-4337" data-mp="3fdbvr16d98cu">
      {isOpen && <div className="fixed inset-0 z-[210] flex items-end md:items-center md:justify-center md:p-6" role="dialog" aria-modal="true" aria-labelledby="order-rating-title" data-id="element-4338" data-mp="3fg6gkttakv6s">
          <motion.button type="button" aria-label="Close rating" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: shouldReduceMotion ? 0 : 0.18,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute inset-0 h-full w-full cursor-default bg-slate-950/45" data-id="element-4339" data-mp="11qei9bgugtgh" />
          <motion.section initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 28
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={shouldReduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 18
      }} transition={{
        duration: shouldReduceMotion ? 0 : 0.24,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative w-full rounded-t-2xl bg-white px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4 shadow-2xl md:max-w-md md:rounded-2xl md:p-7" data-id="element-4340" data-mp="ixamnkk2g6j8">
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-gray-200 md:hidden" data-id="element-4341" data-mp="3mg84c3ir1zjd" />
            <div className="flex items-start justify-between gap-4" data-id="element-4342" data-mp="3qjwg6yeezhaf">
              <div data-id="element-4343" data-mp="3ck53r23jq8yj">
                <p className="text-xs font-semibold text-[#1E2B55]" data-id="element-4344" data-mp="3ckn86m0d3thj">ORDER #{orderNumber}</p>
                <h2 id="order-rating-title" className="mt-1 text-xl font-bold tracking-tight text-gray-900" data-id="element-4345" data-mp="3hjhnhdz4ezww">How was your experience?</h2>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900" data-id="element-4346" data-mp="e1q2ijjejg87">
                <XIcon className="h-5 w-5" data-id="element-4347" data-mp="4b4yuhovflif" />
              </button>
            </div>

            <p className="mt-3 text-sm leading-6 text-gray-600" data-id="element-4348" data-mp="s0z5uimiefur">Your feedback helps us improve every order and service.</p>

            <div className="mt-6 flex justify-between gap-2" role="radiogroup" aria-label="Rate your order" data-id="element-4349" data-mp="wszdkx8tmd1w">
              {[1, 2, 3, 4, 5].map(value => {
            const isSelected = value <= rating;
            return <button key={value} type="button" role="radio" aria-checked={rating === value} aria-label={`${value} out of 5 stars`} onClick={() => setRating(value)} className={`flex h-12 w-12 items-center justify-center rounded-full border transition-[background-color,border-color,color,transform] duration-150 ${isSelected ? 'border-[#1E2B55] bg-[#1E2B55] text-white' : 'border-gray-200 bg-white text-gray-400 hover:border-[#1E2B55] hover:text-[#1E2B55]'}`} data-id="element-4350" data-mp="2chfcnlastm3z">
                    <StarIcon className="h-5 w-5" fill={isSelected ? 'currentColor' : 'none'} data-id="element-4351" data-mp="3izc1h0wte2s5" />
                  </button>;
          })}
            </div>

            <button type="button" onClick={submitRating} disabled={rating === 0} className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#1E2B55] px-4 text-sm font-bold text-white transition-colors duration-150 hover:bg-[#162041] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500" data-id="element-4352" data-mp="3e2lnqk8hfj9w">
              <CheckIcon className="h-4 w-4" data-id="element-4353" data-mp="s0he777ia5zi" />
              Submit rating
            </button>
          </motion.section>
        </div>}
    </AnimatePresence>;
}